import { useLazyQuery, useMutation } from '@apollo/client';
import type { LocationState } from '@openx/components-deal/lib/DealForm/DealForm.types';
import type { DealProviderConfig } from '@openx/components-deal/lib/utils/context/DealContext';
import type { DealFormData, DealFormDataWithTargeting, OADealState } from '@openx/types/deal';
import {
  BudgetMode,
  type ExchangeDealCreateParams,
  type ExchangeDealUpdateParams,
  type FrequencyCappingInput,
  type PacingParams,
} from '@openx/types/gqlTypes';
import { BUDGET_MODE } from '@openx/types/monetizing';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { mergeDeep } from '@openx/utils/lib/mergeDeep';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { DV360_DEMAND_PARTNER_ID } from '../../../config';
import { CreateDealAndPackageDocument } from '../../../graphql/mutation/deals/CreateDealAndPackage.generated';
import { UpdateDealAndPackageDocument } from '../../../graphql/mutation/deals/UpdateDealAndPackage.generated';
import { GetDealDocument } from '../../../graphql/query/deals/GetDeal.generated';
import { GetPackageDocument } from '../../../graphql/query/packages/GetPackage.generated';
import { formatDealState } from '../../../modules/deals/utils/formatDealState';
import { dealFormState } from '../../../modules/graphql/cache';
import { NestedErrorCode } from '../../../modules/graphql/types';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling';
import { transformPlatformShareToDecimal } from './platformShareTransform';
import { transformFrequencyCapping } from './transformFrequencyCapping';
import { useCanEditSegmentPartnerPlatformShare } from './useCanEditSegmentPartnerPlatformShare';

const isDebug = false;

const IdOptions = {
  CUSTOM: 'CUSTOM',
  EXTERNAL: 'EXTERNAL',
  GENERATED: 'GENERATED',
} as const;

export const appendPacingData = (
  dealFormData: ExchangeDealCreateParams | ExchangeDealUpdateParams,
  rawDeal: Partial<
    Pick<
      OADealState,
      'pacing_mode' | 'monetizing_budget' | 'monetizing_region' | 'budget_type' | 'budget_mode' | 'cadence_type'
    >
  >
): void => {
  const { monetizing_budget, pacing_mode, monetizing_region, budget_type, budget_mode, cadence_type } = rawDeal;

  dealFormData.pacing = {
    ...(budget_mode && {
      budget_mode: budget_mode === 'cadence' ? BudgetMode.Cadence : BudgetMode.CampaignFlightTime,
    }),
    ...(dealFormData.start_date !== undefined && { flight_start: dealFormData.start_date }),
    ...(dealFormData.end_date !== undefined && { flight_end: dealFormData.end_date }),
    ...(pacing_mode && { mode: pacing_mode.toUpperCase() }),
    ...(monetizing_region && { region: monetizing_region }),
    ...(budget_type && { budget_type: budget_type.toUpperCase() }),
    ...(budget_mode === BUDGET_MODE.CADENCE &&
      monetizing_budget && {
        budget: null,
        cadences: [{ budget: monetizing_budget.replaceAll(' ', ''), type: cadence_type?.toUpperCase() }],
      }),
    ...(budget_mode === BUDGET_MODE.CAMPAIGN_FLIGHT_TIME &&
      monetizing_budget && { budget: monetizing_budget.replaceAll(' ', ''), cadences: [] }),
  } as PacingParams;
};

const emptyState = { currentDeal: null, error: undefined };

export const useDealFormActions: DealProviderConfig['useDealFormActions'] = <
  T extends DealFormData = DealFormDataWithTargeting,
  D extends Record<string, unknown> = Record<string, unknown>,
>() => {
  const [addDealAndPackage] = useMutation(CreateDealAndPackageDocument);
  const { enqueueSnackbar } = useSnackbar();
  const [updateDealAndPackage] = useMutation(UpdateDealAndPackageDocument);
  const [getDeal] = useLazyQuery(GetDealDocument, { fetchPolicy: 'network-only' });
  const [getPackage] = useLazyQuery(GetPackageDocument);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  // Users who cannot edit the platform fees only read the shares of their own partner, so sending them back would
  // overwrite everyone else's shares (or be refused). Leaving the field out keeps the saved shares.
  const canEditSegmentPartnerPlatformShare = useCanEditSegmentPartnerPlatformShare();

  const locationState: LocationState = useLocation().state;
  const isFromDealPrompt = !!locationState?.dsInitialDealFormPreset;

  useEffect(
    () => () => {
      dealFormState()({ ...emptyState, loading: false });
    },
    []
  );

  const navigate = useNavigate();

  const errorCallback = useCallback(
    (error: { code: NestedErrorCode; path?: string; message?: string }) => {
      if (error.code === NestedErrorCode.NOT_UNIQUE_ERROR && error.path === 'name') {
        enqueueSnackbar(t('Deal with such name already exists.'), { variant: 'error' });
        return true;
      }

      if (error.code === NestedErrorCode.VALIDATION_ERROR && error.message) {
        enqueueSnackbar(error.message, { variant: 'error' });
        return true;
      }

      return false;
    },
    [enqueueSnackbar, t]
  );

  useLocalErrorHandling(UpdateDealAndPackageDocument, errorCallback);
  useLocalErrorHandling(CreateDealAndPackageDocument, errorCallback);

  const getDealFormData = (id: string | undefined) => {
    dealFormState()({ ...emptyState, loading: true });
    if (!id) {
      dealFormState()({ ...emptyState, loading: false });
      return;
    }
    getDeal({ variables: { id } }).then(({ data: { dealById: dealRaw } = {} }) => {
      const dealState = formatDealState(
        dealRaw,
        {
          dv360DemandPartnerId: `${DV360_DEMAND_PARTNER_ID}`,
        },
        null
      );

      if (dealState) {
        dealFormState()({ ...emptyState, currentDeal: dealState, loading: false });
      } else {
        dealFormState()({ ...emptyState, loading: false });
      }
    });
  };

  const submitDealForm = async ({
    dealId,
    deal,
    onSuccess,
  }: {
    dealId: string | undefined;
    deal: D;
    fallbackValues?: T;
    onSuccess?: () => void;
  }) => {
    const currentState = dealFormState()();
    const dealData = deal as unknown as Partial<DealFormData> & { package_create_error: boolean };

    if (dealData.package_create_error) {
      dealFormState()({
        ...currentState,
        loading: false,
      });

      return;
    }

    if (dealData.name) dealData.name = dealData.name.trim();

    let exchange_package_id: string | undefined;

    // Getting id from pacakge uid
    if (dealData.package_uid) {
      const { data } = await getPackage({
        variables: { id: dealData.package_uid },
      });

      if (!data?.packageById?.id) {
        dealFormState()({
          ...currentState,
          currentDeal: currentState.currentDeal
            ? {
                ...currentState.currentDeal,
                package_uid: undefined,
              }
            : null,
          loading: false,
        });

        enqueueSnackbar(t('Package is not found.'), { variant: 'error' });
        enqueueSnackbar(
          t(
            "Failed to get package details. Sometimes creating the package takes a while. Please try saving the deal again in a few minutes after selecting it from the list utilizing the 'Use an Existing Package' option ."
          ),
          { variant: 'error' }
        );

        return;
      }

      exchange_package_id = data?.packageById?.id ?? undefined;
    }

    //move single selected buyer_id(dv360 demand partner) to buyer_ids[] as required by API
    dealData.deal_participants?.forEach(demandPartner => {
      if (demandPartner.buyer_id) {
        demandPartner.buyer_ids = [demandPartner.buyer_id];
      }
    });

    // always pass "third_party_fees_config" property, otherwise the "currentState.currentDeal" will override it
    if (!Object.hasOwn(dealData, 'third_party_fees_config')) {
      dealData.third_party_fees_config = undefined;
    }

    const newDeal: Required<OADealState> = mergeDeep(currentState.currentDeal, dealData, false);

    if (dealData.rbo_config) {
      newDeal.rbo_config = dealData.rbo_config;
    }

    isDebug && dealFormState()({ ...currentState, loading: false });
    !isDebug && dealFormState()({ ...currentState, loading: true });

    if (!dealId) {
      const { deal_participants, ...rawDeal } = newDeal;
      const id: {
        dealIdPrefix?: string;
      } = {
        dealIdPrefix: undefined,
      };

      const createParticipants = deal_participants.map(part => ({
        brand_ids: part.brand_ids,
        buyer_ids: part.amazon_sync_marketplace_enabled
          ? []
          : part.main_buyer_id
            ? [...part.buyer_ids, part.main_buyer_id]
            : part.buyer_ids,
        demand_partner: `${part.demand_partner}`,
        main_buyer_id: part.main_buyer_id,
      }));

      const dealCreate: ExchangeDealCreateParams = {
        account_uid: rawDeal.account_uid,
        agency_salesforce_id: rawDeal.agency_salesforce_id,
        brand_salesforce_id: rawDeal.brand_salesforce_id,
        created_with: 'OpenAudience UI',
        curated_deal_buyer_discount_enabled: rawDeal.curated_deal_buyer_discount_enabled,
        currency: rawDeal.currency,
        // Only set when the deal is created via the Deal Prompt route
        ...(isFromDealPrompt && { deal_creation_context: 'deal_prompt' }),
        deal_participants: createParticipants,
        deal_price: rawDeal.deal_price,
        deal_source: rawDeal.deal_source,
        enable_dv360_inventory_package: rawDeal.enable_dv360_inventory_package,
        end_date: rawDeal.end_date,
        expected_ad_category: rawDeal.expected_ad_category,
        fixed_cpm_enabled: rawDeal.fixed_cpm_enabled,
        frequency_capping: transformFrequencyCapping(rawDeal?.frequency_capping || []) as
          | FrequencyCappingInput[]
          | null
          | undefined,
        name: rawDeal.name ?? '',
        pdp_config: rawDeal.pdp_config,
        pmp_deal_type: rawDeal.pmp_deal_type,
        rbo_config: rawDeal.rbo_config,
        start_date: rawDeal.start_date,
        third_party_fees_config: rawDeal.third_party_fees_config,
        segment_partner_revenue_method: rawDeal.segment_partner_revenue_method ?? null,
        ...(canEditSegmentPartnerPlatformShare && {
          segment_partner_platform_share: transformPlatformShareToDecimal(
            rawDeal.segment_partner_platform_share
          ) as ExchangeDealCreateParams['segment_partner_platform_share'],
        }),
      };

      if (rawDeal.is_pacing_enabled) {
        appendPacingData(dealCreate, rawDeal);
      }

      if (newDeal?.deal_id_type_radio === IdOptions.CUSTOM) {
        dealCreate.deal_id = newDeal?.custom_deal_id;
      }
      if (newDeal?.deal_id_type_radio === IdOptions.GENERATED && newDeal?.generated_deal_id_prefix !== 'Default') {
        id.dealIdPrefix = newDeal?.generated_deal_id_prefix ?? undefined;
      }
      if (newDeal?.deal_id_type_radio === IdOptions.EXTERNAL) {
        dealCreate.deal_id = newDeal?.external_deal_id;
      }

      !isDebug &&
        (await addDealAndPackage({
          refetchQueries: ['GetDealsList', 'GetPackageUsedBy', 'GetDeal'],
          variables: {
            deal: dealCreate,
            dealIdPrefix: id.dealIdPrefix,
            exchange_package_id,
            package: {},
          },
        })
          .then(res => {
            const { createDealAndPackage } = res.data ?? {};
            if (createDealAndPackage?.uid) {
              enqueueSnackbar('Deal successfully created', { variant: 'success' });
              onSuccess?.();
              navigate(`/deals/${createDealAndPackage.uid}`);
            }
          })
          .catch(() => {
            dealFormState()({
              ...currentState,
              loading: false,
            });
          }));
    } else {
      const { deal_participants, ...rawDeal } = newDeal;
      const updateParticipants = deal_participants
        ? deal_participants.map(part => ({
            brand_ids: part.brand_ids,
            buyer_ids: part.amazon_sync_marketplace_enabled
              ? []
              : part.main_buyer_id
                ? [...part.buyer_ids, part.main_buyer_id]
                : part.buyer_ids,
            demand_partner: `${part.demand_partner}`,
            main_buyer_id: part.main_buyer_id,
          }))
        : deal_participants;

      // TODO: UIG-540 Concert to exclusionary partern
      /*
      const { targeting, nonDealField, ...rawDeal } = newDeal;
      const dealMutation: ExchangeDealUpdateParams = {...rawDeal};
       */

      const {
        account_uid,
        deal_price,
        end_date,
        name,
        pmp_deal_type,
        start_date,
        status,
        third_party_fees_config,
        expected_ad_category,
        fixed_cpm_enabled,
        rbo_config,
        deal_source,
        agency_salesforce_id,
        brand_salesforce_id,
        curated_deal_buyer_discount_enabled,
        frequency_capping,
        pdp_config,
        segment_partner_revenue_method,
        segment_partner_platform_share,
      } = rawDeal;

      const dealMutation: ExchangeDealUpdateParams = {
        account_uid,
        deal_participants: updateParticipants,
        deal_price,
        deal_source,
        end_date,
        expected_ad_category,
        fixed_cpm_enabled,
        frequency_capping: transformFrequencyCapping(frequency_capping || []) as
          | FrequencyCappingInput[]
          | null
          | undefined,
        name,
        pdp_config,
        pmp_deal_type,
        rbo_config,
        start_date,
        status,
        third_party_fees_config,
        agency_salesforce_id,
        brand_salesforce_id,
        curated_deal_buyer_discount_enabled,
        segment_partner_revenue_method: segment_partner_revenue_method ?? null,
        ...(canEditSegmentPartnerPlatformShare && {
          segment_partner_platform_share: transformPlatformShareToDecimal(
            segment_partner_platform_share
          ) as ExchangeDealUpdateParams['segment_partner_platform_share'],
        }),
      };

      const isPacingEnabled: boolean = rawDeal.is_pacing_enabled;

      if (isPacingEnabled) {
        appendPacingData(dealMutation, rawDeal);
      } else {
        dealMutation.pacing = null;
      }

      !isDebug &&
        (await updateDealAndPackage({
          refetchQueries: ['GetDealsList', 'GetPackageUsedBy', 'GetDeal'],
          variables: {
            deal: dealMutation,
            exchange_package_id,
            updateDealAndPackageId: dealId,
            //Do not pass package object here as it will cause
            //package updates which is not possible from here anymore
          },
        })
          .then(res => {
            const { updateDealAndPackage } = res.data ?? {};

            if (updateDealAndPackage) {
              if (isPacingEnabled) {
                void queryClient.invalidateQueries({ queryKey: [QueryKeys.MONETIZING] });
              }
              enqueueSnackbar(t('Deal successfully updated'), { variant: 'success' });
              onSuccess?.();
              dealFormState()({
                ...currentState,
                currentDeal: newDeal,
                loading: false,
              });
              getDeal({ fetchPolicy: 'network-only', variables: { id: dealId } });
              navigate(`/deals/${dealId}`);
            }
          })
          .catch(() => {
            dealFormState()({
              ...currentState,
              loading: false,
            });
          }));
    }
  };

  const skipDealUpdate = useCallback(
    (dealUid: string) => {
      navigate(`/deals/${dealUid}`);
    },
    [navigate]
  );

  return { getDealFormData, submitDealForm, skipDealUpdate };
};
